/**
 * useCartCount — header badge counter, backed by `GET /api/cart/summary`
 * via `useCartSummary`. Exposes both the total item count AND the cart
 * subtotal so callers (header badge, mobile bar, checkout teasers) can
 * read everything from one hook.
 *
 * Backwards-compat: the returned object is also coerced to its numeric
 * `count` via `valueOf()`, so legacy code like
 *   const cartCount = useCartCount();          // still works
 *   if (cartCount > 0) ...                     // still works
 *   <Badge>{cartCount}</Badge>                 // still works
 * keeps functioning, while new code can do:
 *   const { count, total, refresh } = useCartCount();
 */
import useCartSummary from './useCartSummary';

export interface CartCount {
  /** Total items across all stores */
  count: number;
  /** Cart subtotal across all stores (₹) */
  total: number;
  /** Force a refetch of the summary */
  refresh: () => void;
  /** Coerce to the numeric count for legacy `useCartCount() as number` callers */
  valueOf(): number;
  toString(): string;
}

export function useCartCount(): CartCount {
  const { item_count, total, refresh } = useCartSummary();
  return {
    count: item_count,
    total,
    refresh,
    valueOf() {
      return item_count;
    },
    toString() {
      return String(item_count);
    },
  };
}

export default useCartCount;
